import type { ButtonHTMLAttributes, ReactNode } from 'react'

export type ButtonSize = 'default' | 'compact' | 'hero' | 'quote' | 'section' | 'sectionInverse'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode
  size?: ButtonSize
}

const variantClasses: Record<Exclude<ButtonSize, 'default'>, { face: string; root: string; text: string }> = {
  compact: {
    root: 'h-[39px] w-[122px]',
    face: 'ml-[6px] mt-[4px] h-[31px] w-[108px] justify-center px-2 text-[16px]',
    text: 'whitespace-nowrap',
  },
  hero: {
    root: 'h-[39px] w-[214px]',
    face: 'ml-[6px] mt-[4px] h-[31px] w-[200px] justify-start pl-[9px] pr-[24px] text-[17px]',
    text: 'origin-left scale-x-[0.94] whitespace-nowrap',
  },
  quote: {
    root: 'h-[39px] w-[246px]',
    face: 'ml-[6px] mt-[4px] h-[31px] w-[232px] justify-start pl-[10px] pr-[24px] text-[17px]',
    text: 'whitespace-nowrap',
  },
  section: {
    root: 'h-[45px] w-[275px]',
    face: 'ml-[6px] mt-[4px] h-[33px] w-[260px] justify-center px-[26px] text-[18px]',
    text: 'whitespace-nowrap',
  },
  sectionInverse: {
    root: 'h-[45px] w-[275px]',
    face: 'ml-[6px] mt-[4px] h-[33px] w-[260px] justify-center px-[26px] text-[18px]',
    text: 'whitespace-nowrap',
  },
}

export default function Button({ children, className = '', size = 'default', type = 'button', ...props }: ButtonProps) {
  const variant = size === 'default' ? 'section' : size
  const config = variantClasses[variant]
  const compact = variant === 'compact'
  const inverse = variant === 'sectionInverse'

  return (
    <button
      type={type}
      className={`group relative inline-flex cursor-pointer border-0 bg-transparent p-0 font-sans ${config.root} ${className}`.trim()}
      {...props}
    >
      <span aria-hidden="true" className="absolute bottom-[7px] left-0 right-[10px] top-0 box-border border-[3px] border-[#131a54]" />
      <span aria-hidden="true" className="absolute bottom-0 left-[10px] right-0 top-[7px] box-border border-[3px] border-[#131a54]" />
      <span className={`relative z-10 inline-flex items-center overflow-hidden font-extrabold leading-none transition-[transform,background-color] group-hover:-translate-y-0.5 ${config.face} ${inverse ? 'bg-[#e9ebf6] text-[#131a54] group-hover:bg-white' : 'bg-[#b74736] text-[#e9ebf6] group-hover:bg-[#c65b49]'}`}>
        <span className={config.text}>{children}</span>
        {!compact && (
          <svg aria-hidden="true" className="absolute right-[8px] top-1/2 h-[14px] w-[8px] -translate-y-1/2" viewBox="0 0 8 14">
            <path d="m1 1 6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" />
          </svg>
        )}
      </span>
    </button>
  )
}
